import FacetTypes from '@/types/search/enums/facetTypes';
import {
    getFacetOptionCandidates,
    mapBrowsePairsToSearchOptionIds,
    type BrowseCategoryRawFacet,
} from '@/utils/browsePath/browsePath';
import { urlSafeTitle } from '@/utils/urls';

/** Path segment for Subject (art) on SEO search URLs, aligned with category browse dimension key. */
export const SEARCH_SEO_SUBJECT_PATH_SEGMENT = 'subject';

export type ParsedSearchSeoPath =
    | { kind: 'plain'; keyword: string; subjectValueSlug?: string }
    | { kind: 'location'; region: string; citySlug: string; keyword: string; subjectValueSlug?: string };

/**
 * Parses `/search/:keyword/` and `/search/:keyword/subject/:slug/` (and localized variants).
 * Returns null when the path is not a recognized SEO search shape.
 */
export const parseSearchSeoPath = (pathname: string): ParsedSearchSeoPath | null => {
    const normalized = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    const parts = normalized.split('/').filter(Boolean);
    if (parts[0] !== 'search') {
        return null;
    }

    let subjectValueSlug: string | undefined;
    let baseParts = parts;
    if (parts.length >= 2 && parts[parts.length - 2] === SEARCH_SEO_SUBJECT_PATH_SEGMENT) {
        subjectValueSlug = parts[parts.length - 1];
        baseParts = parts.slice(0, -2);
    }

    if (baseParts.length === 2) {
        return { kind: 'plain', keyword: baseParts[1], subjectValueSlug };
    }
    if (baseParts.length === 4) {
        return {
            kind: 'location',
            region: baseParts[1],
            citySlug: baseParts[2],
            keyword: baseParts[3],
            subjectValueSlug,
        };
    }
    return null;
};

export const buildSeoSearchListingPath = (parsed: ParsedSearchSeoPath): string => {
    const subjectTrail = parsed.subjectValueSlug
        ? `/${SEARCH_SEO_SUBJECT_PATH_SEGMENT}/${parsed.subjectValueSlug}`
        : '';
    if (parsed.kind === 'plain') {
        return `/search/${parsed.keyword}${subjectTrail}/`;
    }
    return `/search/${parsed.region}/${parsed.citySlug}/${parsed.keyword}${subjectTrail}/`;
};

/**
 * Resolves `/subject/:slug/` on an SEO search URL to search-party `artSubject` option ids.
 * Numeric slugs are treated as option ids. Otherwise uses the same slug→id rules as category browse paths.
 */
export const resolveSearchSeoPathSubjectToBrowseFacetIds = (
    pathname: string,
    rawFacets: BrowseCategoryRawFacet[]
): Record<string, string[]> | undefined => {
    const parsed = parseSearchSeoPath(pathname);
    if (!parsed?.subjectValueSlug) {
        return undefined;
    }
    const slug = parsed.subjectValueSlug;
    if (/^\d+$/.test(slug)) {
        return { [FacetTypes.ART_SUBJECT_ID]: [slug] };
    }
    const out = mapBrowsePairsToSearchOptionIds(
        [{ dimensionKey: SEARCH_SEO_SUBJECT_PATH_SEGMENT, valueSlug: slug }],
        rawFacets
    );
    return out[FacetTypes.ART_SUBJECT_ID]?.length ? out : undefined;
};

/**
 * Builds the `subject` path tail for SEO search URLs from `artSubject` query params and facet metadata.
 * Multiple ids use `~`, consistent with category browse multi-select slugs.
 */
export const getArtSubjectPathSlugForSeoUrl = (
    params: { artSubject?: unknown },
    rawFacets: BrowseCategoryRawFacet[]
): string | undefined => {
    const raw = params.artSubject;
    if (raw === undefined || raw === null || raw === '') {
        return undefined;
    }
    const ids = String(raw)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    if (ids.length === 0) {
        return undefined;
    }
    const facet = rawFacets.find((f) => f.id === FacetTypes.ART_SUBJECT_ID);
    const candidates = getFacetOptionCandidates(facet);
    const slugs = ids.map((id) => {
        const opt = candidates.find((o) => String(o.id) === id);
        if (opt?.name) {
            return urlSafeTitle(opt.name);
        }
        return id;
    });
    return slugs.join('~');
};
